// Shared component (TS-2.5.03). A minimal, focus-managed confirm/cancel modal - the one place in
// this batch a blocking UI is appropriate (decisions.md's staleness/duplicate-id guidance is about
// SAVE RESPONSES specifically; asking "discard unsaved changes?" before a navigation the user
// explicitly chose is a different, ordinary confirm-before-destructive-action pattern, and the
// native semantics an alertdialog role provides - it interrupts, which is exactly what "you are
// about to lose work" should do).
//
// Focus management (D-241): Escape invokes onCancel, same as clicking the cancel button; Tab and
// Shift+Tab are trapped within the dialog while it is open (the two buttons are the whole
// focusable set, so trapping just means Tab/Shift+Tab from either button lands on the other);
// initial focus lands on the cancel button, per convention for destructive confirms (mirrors the
// button order/labels - cancel is first and is the "safe" default); and focus returns to whichever
// element triggered the dialog once it closes, the same contract ProjectChip.tsx's own popover
// already honours for its Escape-closes-and-returns-focus behaviour.
import { useEffect, useRef, type KeyboardEvent } from "react";
import "./confirmDialog.css";

export interface ConfirmDialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly body: string;
  readonly confirmLabel: string;
  readonly cancelLabel: string;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

export function ConfirmDialog({ open, title, body, confirmLabel, cancelLabel, onConfirm, onCancel }: ConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<Element | null>(null);

  useEffect(() => {
    if (open) {
      triggerRef.current = document.activeElement;
      cancelRef.current?.focus();
    } else if (triggerRef.current instanceof HTMLElement) {
      triggerRef.current.focus();
      triggerRef.current = null;
    }
  }, [open]);

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      onCancel();
      return;
    }
    if (event.key !== "Tab") return;

    const first = cancelRef.current;
    const last = confirmRef.current;
    if (!first || !last) return;

    if (event.shiftKey) {
      if (document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }
    } else if (document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  if (!open) return null;

  return (
    <div className="eg-confirm-dialog__overlay">
      <div
        className="eg-confirm-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="eg-confirm-title"
        aria-describedby="eg-confirm-body"
        onKeyDown={handleKeyDown}
      >
        <h2 id="eg-confirm-title">{title}</h2>
        <p id="eg-confirm-body">{body}</p>
        <div className="eg-confirm-dialog__actions">
          <button type="button" className="eg-confirm-dialog__cancel" ref={cancelRef} onClick={onCancel}>
            {cancelLabel}
          </button>
          <button type="button" className="eg-confirm-dialog__confirm" ref={confirmRef} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
